import {
  BigDecimal,
  BigDecimalish,
  millisToSeconds,
  PlaceOrderParams,
  PlaceTriggerOrderParams,
  toBigDecimal,
} from '@vertex-protocol/client';
import {
  getExpirationTimestamp,
  getOrderNonce,
  OrderExpirationType,
} from '@vertex-protocol/contracts';
import {
  toPrintableObject,
  VERTEX_PRODUCT_DECIMALS,
} from '@vertex-protocol/utils';
import { packOrderAppendix } from '@nadohq/shared';
import { baseClientEnv } from 'common/environment/baseClientEnv';
import { ExecutePlaceOrderParams } from 'client/hooks/execute/placeOrder/types';
import { ValidExecuteContext } from 'client/hooks/execute/util/useExecuteInValidContext';
import { useAllMarketsStaticData } from 'client/hooks/markets/marketsStaticData/useAllMarketsStaticData';
import { useOrderbookAddresses } from 'client/hooks/query/markets/useOrderbookAddresses';
import { useGetRecvTime } from 'client/hooks/util/useGetRecvTime';
import { roundToIncrement, roundToString } from 'client/utils/rounding';
import { addDays, getTime } from 'date-fns';
import { useCallback } from 'react';

export function usePlaceOrderMutationFn() {
  const { data: marketDataByProductId } = useAllMarketsStaticData();
  const { data: orderbookAddresses } = useOrderbookAddresses();
  const getRecvTime = useGetRecvTime();

  return useCallback(
    async (params: ExecutePlaceOrderParams, context: ValidExecuteContext) => {
      // Round amount & price
      const increments = marketDataByProductId?.allMarkets[params.productId];
      const roundedAmount = toMutationAmountInput(
        params.amount,
        // Undefined size increment will skip rounding
        params.allowAnyOrderSizeIncrement
          ? undefined
          : increments?.sizeIncrement,
      );
      const roundedPrice = toMutationPriceInput(
        params.price,
        increments?.priceIncrement,
      );

      const nonce = await (async () => {
        const recvTime = await getRecvTime();
        // Use getOrderNonce for all order types — it encodes recv_time
        // in the upper bits which the trigger service validates.
        return getOrderNonce(recvTime);
      })();

      // Create the relevant expiration type depending on order type
      const expirationType = ((): OrderExpirationType => {
        if (params.priceType === 'limit') {
          switch (params.timeInForceType) {
            case 'good_until':
              return params.postOnly ? 'post_only' : 'default';
            case 'fok':
              return 'fok';
            case 'ioc':
              return 'ioc';
            default:
              return 'default'; // In case timeInForceType is not defined and It's limit order.
          }
        }
        // Stop & market order
        return 'fok';
      })();

      const expirationTime = (() => {
        const nowMillis = Date.now();

        if (
          params.priceType === 'limit' &&
          params.timeInForceType === 'good_until' &&
          params.timeInForceInDays
        ) {
          return millisToSeconds(
            getTime(addDays(nowMillis, params.timeInForceInDays.toNumber())),
          );
        }

        // No explicit expiration — use a far-future time (90 days).
        // For trigger orders (TP/SL), the order must persist until triggered.
        return millisToSeconds(
          getTime(addDays(nowMillis, 90)),
        );
      })();

      // Pack the order appendix with builder fee and order execution metadata.
      // For trigger orders, set triggerType so the trigger service recognizes them.
      const isTriggerOrder = params.priceType === 'stop';
      const { builderId, builderFeeRate } = baseClientEnv;
      const baseAppendixOptions = {
        orderExecutionType: expirationType,
        ...(params.reduceOnly ? { reduceOnly: true } : {}),
        ...(isTriggerOrder ? { triggerType: 'price' as const } : {}),
        ...(builderId !== undefined
          ? { builder: { builderId, builderFeeRate } }
          : {}),
      };
      const appendix = packOrderAppendix(baseAppendixOptions);

      // Order is common across engine & trigger.
      // For trigger orders: don't encode order type in the expiration bitmask
      // (FOK sets bit 63 which exceeds i64::MAX). The appendix carries the
      // execution type and triggerType instead.
      const order: PlaceOrderParams['order'] = {
        subaccountName: params.subaccountName ?? context.subaccount.name,
        subaccountOwner: context.subaccount.address,
        price: roundedPrice,
        amount: roundedAmount,
        expiration: getExpirationTimestamp({
          type: isTriggerOrder ? 'default' : expirationType,
          expirationTime,
          reduceOnly: params.reduceOnly,
        }),
        appendix,
      };

      const sharedParams:
        | PlaceTriggerOrderParams
        | PlaceOrderParams = {
        productId: params.productId,
        order,
        nonce,
        verifyingAddr: orderbookAddresses?.[params.productId],
        chainId: context.subaccount.chainId,
        spotLeverage: params.spotLeverage,
      };

      /*
      Use a different mutation for trigger, isolated engine, and regular engine orders
       */

      // Trigger order
      if (params.priceType === 'stop') {
        const roundedTriggerPrice = toMutationPriceInput(
          params.triggerPrice,
          increments?.priceIncrement,
        );

        const triggerOrderParams: PlaceTriggerOrderParams = {
          triggerCriteria: {
            type: 'price',
            criteria: {
              type: params.triggerCriteriaType,
              triggerPrice: toMutationPriceInput(
                roundedTriggerPrice,
                increments?.priceIncrement,
              ),
            },
          },
          ...sharedParams,
        };

        console.log(
          'Placing trigger order',
          toPrintableObject(triggerOrderParams),
        );
        return context.vertexClient.market.placeTriggerOrder(
          triggerOrderParams,
        );
      }

      // Engine iso order but NOT reduce-only
      // The margin is encoded in the order appendix (isolated.margin field).
      // placeIsolatedOrder does not exist on the client — use placeOrder with
      // borrowMargin and a re-packed appendix that includes the isolated margin.
      if (params.iso && !params.reduceOnly) {
        const isoAppendix = packOrderAppendix({
          ...baseAppendixOptions,
          isolated: { margin: toMutationAmountInput(params.iso.margin) },
        });
        const engineIsoOrderParams: PlaceOrderParams = {
          ...sharedParams,
          order: {
            ...sharedParams.order,
            appendix: isoAppendix,
          },
          borrowMargin: params.iso.borrowMargin,
        };

        console.log(
          'Placing isolated engine order',
          toPrintableObject(engineIsoOrderParams),
        );
        return context.vertexClient.market.placeOrder(engineIsoOrderParams);
      }

      // Engine iso reduce-only order (closing an isolated position).
      // The Nado engine does not allow orders from isolated subaccounts, so
      // we use the cross subaccount and include `isolated` in the appendix
      // so the engine targets the isolated position.
      if (params.iso && params.reduceOnly) {
        const isoReduceAppendix = packOrderAppendix({
          ...baseAppendixOptions,
          isolated: { margin: toMutationAmountInput(params.iso.margin) },
        });

        const engineIsoReduceParams: PlaceOrderParams = {
          ...sharedParams,
          order: {
            ...sharedParams.order,
            // Always use the cross subaccount — the Nado engine rejects
            // orders placed from isolated subaccounts.
            subaccountName: context.subaccount.name,
            appendix: isoReduceAppendix,
          },
        };

        console.log(
          'Placing isolated reduce-only engine order',
          toPrintableObject(engineIsoReduceParams),
        );
        return context.vertexClient.market.placeOrder(engineIsoReduceParams);
      }

      // Engine cross order
      const engineOrderParams: PlaceOrderParams = sharedParams;

      console.log('Placing engine order', toPrintableObject(engineOrderParams));
      return context.vertexClient.market.placeOrder(engineOrderParams);
    },
    [getRecvTime, marketDataByProductId?.allMarkets, orderbookAddresses],
  );
}

function toMutationPriceInput(price: BigDecimalish, increment?: BigDecimal) {
  const roundedPrice = increment
    ? roundToIncrement(toBigDecimal(price), increment)
    : toBigDecimal(price);
  // Prevent "e-11" to improve parsing compatibility, as we have X18, the max decimal places we need to support is 18
  return roundToString(roundedPrice, VERTEX_PRODUCT_DECIMALS);
}

function toMutationAmountInput(amount: BigDecimalish, increment?: BigDecimal) {
  const roundedAmount = increment
    ? roundToIncrement(toBigDecimal(amount), increment)
    : toBigDecimal(amount);
  return roundToString(roundedAmount, 0);
}
